import React, { useState, useEffect } from "react"
import { SEOHead } from '../SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { LogOut, FileText, Briefcase, Users, BarChart, MessagesSquare, FileDown, Settings } from 'lucide-react';
import { AddProjectForm } from './AddProjectForm';
import { AddCareerForm } from './AddCareerForm';
import { ViewApplications } from './ViewApplications';
import { ViewEnquiries } from './ViewEnquiries';
import { ManageDocuments } from './ManageDocuments';
import { ManageSiteSettings } from './ManageSiteSettings';
import { hasAdminSession } from '../../services/authSession';

interface AdminDashboardProps {
  onLogout: () => void;
  applications: any[];
  onNavigation?: (path: string) => void;
}

const isTokenValid = hasAdminSession;

export function AdminDashboard({ onLogout, applications, onNavigation }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState("projects");

  // Token check on component mount and route changes
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_access_token');
      if (!token || !isTokenValid(token)) {
        // Agar token invalid hai toh logout kar do
        console.log('Token expired or invalid, logging out...');
        onLogout();
        return;
      }
    };

    // Initial check
    checkAuth();

    // Check on tab changes
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      onLogout();
      return;
    }
  }, [onLogout, activeTab]);

  // URL se tab detect karo - with auth check
  useEffect(() => {
    const checkAuthAndNavigate = () => {
      const token = localStorage.getItem('admin_access_token');
      if (!token || !isTokenValid(token)) {
        onLogout();
        return;
      }

      const path = window.location.pathname;
      if (path === '/admin/dashboard' || path === '/admin') {
        setActiveTab('projects');
        if (onNavigation && path === '/admin') {
          onNavigation('/dashboard');
        }
      } else if (path === '/admin/careers') {
        setActiveTab('careers');
      } else if (path === '/admin/applications') {
        setActiveTab('applications');
      } else if (path === '/admin/enquiries') {
        setActiveTab('enquiries');
      } else if (path === '/admin/documents') {
        setActiveTab('documents');
      } else if (path === '/admin/settings') {
        setActiveTab('settings');
      }
    };

    checkAuthAndNavigate();
  }, [onNavigation, onLogout]);

  // Tab change par URL update karo - with auth check
  const handleTabChange = (value: string) => {
    // Tab change se pehle auth check karo
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      onLogout();
      return;
    }

    setActiveTab(value);

    // URL update karo
    if (onNavigation) {
      const paths: { [key: string]: string } = {
        'projects': '/dashboard',
        'careers': '/careers',
        'applications': '/applications',
        'enquiries': '/enquiries',
        'documents': '/documents',
        'settings': '/settings'
      };
      onNavigation(paths[value] || '/dashboard');
    }
  };

  // Direct URL navigation ke liye functions - with auth check
  const navigateToProjects = () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      onLogout();
      return;
    }
    setActiveTab('projects');
    if (onNavigation) onNavigation('/dashboard');
  };

  const navigateToCareers = () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      onLogout();
      return;
    }
    setActiveTab('careers');
    if (onNavigation) onNavigation('/careers');
  };

  const navigateToApplications = () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      onLogout();
      return;
    }
    setActiveTab('applications');
    if (onNavigation) onNavigation('/applications');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Admin Dashboard - USP Consultancy"
        description="Administration dashboard for USP Consultancy. Restricted access."
        canonical="https://uspconsultancy.com.np/admin/dashboard"
        robots="noindex, nofollow"
      />
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                <img
                  src="/usp.png"
                  alt="USP Consultancy Logo"
                  className="h-16 w-16"
                />              </div>
              <div>
                <h2>Admin Panel</h2>
                <p className="text-xs text-gray-500">USP Consultancy Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Quick Navigation Buttons */}
              <div className="hidden md:flex space-x-2">
                <Button
                  variant={activeTab === 'projects' ? "default" : "outline"}
                  size="sm"
                  onClick={navigateToProjects}
                  className="cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Projects
                </Button>
                <Button
                  variant={activeTab === 'careers' ? "default" : "outline"}
                  size="sm"
                  onClick={navigateToCareers}
                  className="cursor-pointer"
                >
                  <Briefcase className="w-4 h-4 mr-1" />
                  Careers
                </Button>
                <Button
                  variant={activeTab === 'applications' ? "default" : "outline"}
                  size="sm"
                  onClick={navigateToApplications}
                  className="cursor-pointer"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Applications
                </Button>
                <Button
                  variant={activeTab === 'enquiries' ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    const token = localStorage.getItem('admin_access_token');
                    if (!token || !isTokenValid(token)) return onLogout();
                    setActiveTab('enquiries');
                    if (onNavigation) onNavigation('/enquiries');
                  }}
                  className="cursor-pointer"
                >
                  <MessagesSquare className="w-4 h-4 mr-1" />
                  Enquiries
                </Button>
              </div>
              <Button variant="outline" onClick={onLogout} className="cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs for Different Admin Functions */}
        <Card>
          <CardHeader>
            <CardTitle>Management Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <div className="relative mb-6">
                {/* Left Scroll Button */}
                <button
                  type="button"
                  className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border rounded-full shadow-md p-1 z-10 sm:flex hidden"
                  onClick={() => {
                    const container = document.getElementById("tabs-scroll-container");
                    if (container) container.scrollBy({ left: -150, behavior: "smooth" });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Scrollable Tabs */}
                <div
                  id="tabs-scroll-container"
                  className="flex overflow-x-auto no-scrollbar space-x-2 px-4 sm:px-12 scroll-smooth"
                >
                  <TabsList className="flex space-x-2 min-w-max bg-transparent border-0 shadow-none">
                    {[
                      { value: 'projects', label: 'Add Project', icon: FileText },
                      { value: 'careers', label: 'Add Career', icon: Briefcase },
                      { value: 'applications', label: 'View Applications', icon: Users },
                      { value: 'enquiries', label: 'View Enquiries', icon: MessagesSquare },
                      { value: 'documents', label: 'Documents', icon: FileDown },
                      { value: 'settings', label: 'Site Settings', icon: Settings }
                    ].map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.value;
                      return (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          className={`flex-shrink-0 px-4 py-2 rounded-lg border text-sm cursor-pointer flex items-center transition-all duration-200
    ${isActive
                              ? 'text-black border-gray-300 shadow-lg scale-400'
                              : 'text-black border-gray-300 hover:shadow-md hover:scale-105'
                            }
  `}
                        >
                          <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-black' : 'text-black'}`} />
                          {tab.label}
                        </TabsTrigger>

                      );
                    })}
                  </TabsList>
                </div>

                {/* Right Scroll Button */}
                <button
                  type="button"
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-white border rounded-full shadow-md p-1 z-10 sm:flex hidden"
                  onClick={() => {
                    const container = document.getElementById("tabs-scroll-container");
                    if (container) container.scrollBy({ left: 150, behavior: "smooth" });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Tabs Content */}
              <TabsContent value="projects">
                <AddProjectForm />
              </TabsContent>

              <TabsContent value="careers">
                <AddCareerForm />
              </TabsContent>

              <TabsContent value="applications">
                <ViewApplications applications={applications} />
              </TabsContent>

              <TabsContent value="enquiries">
                <ViewEnquiries />
              </TabsContent>

              <TabsContent value="documents">
                <ManageDocuments />
              </TabsContent>

              <TabsContent value="settings">
                <ManageSiteSettings />
              </TabsContent>
            </Tabs>

          </CardContent>
        </Card>
      </main>
    </div>
  );
}